<script setup>
import { ref, computed, watch, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { vOnClickOutside } from '@vueuse/components';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Icon from 'next/icon/Icon.vue';
import SpacesAPI from 'dashboard/api/spaces';
import { useStore } from 'vuex';
import { isSuggestableUnit, normalizeTier } from './pricingTiers';

const props = defineProps({
  row: { type: Object, default: null },
  price: { type: Object, default: null },
  itemOptions: { type: Array, default: () => [] },
});
const emit = defineEmits(['save', 'close']);
const UNITS = [
  'per_day',
  'per_hour',
  'per_guest',
  'per_hour_per_guest',
  'per_quantity',
  'per_event',
];
const CURRENCIES = [
  'EUR',
  'USD',
  'GBP',
  'CHF',
  'SEK',
  'NOK',
  'DKK',
  'PLN',
  'CZK',
];

const { t } = useI18n();
const store = useStore();

const dialogRef = ref(null);

const form = ref({
  name: '',
  unit: 'per_hour',
  currency: 'EUR',
  price: '',
  tax_rate: '',
  is_default: true,
});

const isEditingPrice = computed(() => !!props.price?.id);
const isNewItem = computed(() => !props.row?.pricingItem?.id);
const isStandalone = computed(() => props.row?.type === 'standalone');

const selectedItemKey = ref('');
const itemSearch = ref('');
const showItemList = ref(false);

const suggesting = ref(false);
const suggestionNotes = ref([]);
const suggestionError = ref('');
const suggestionResult = ref(null);
const priceFromSuggestion = ref(false);
const applyingSuggestion = ref(false);

const selectedItem = computed(
  () =>
    props.itemOptions.find(option => option.key === selectedItemKey.value) ||
    null
);

const spaceIdForSuggestion = computed(() => {
  if (props.row?.type === 'space') {
    return (
      props.row.sourcePayload?.space_id ||
      props.row.pricingItem?.space_id ||
      null
    );
  }
  if (isStandalone.value && selectedItem.value?.type === 'space') {
    return selectedItem.value.sourcePayload?.space_id || null;
  }
  return null;
});

const linkedSpace = computed(() => {
  if (!spaceIdForSuggestion.value) return null;
  const spaces = store.getters['spaces/getSpaces'] || [];
  return spaces.find(space => space.id === spaceIdForSuggestion.value) || null;
});

const spaceTier = computed(() => {
  const tier = normalizeTier(linkedSpace.value?.tier || props.row?.tier);
  return tier && tier !== 'none' ? tier : null;
});

const spaceHasCapacity = computed(() => {
  const space = linkedSpace.value;
  if (space) {
    return [
      space.max_capacity_standing,
      space.max_capacity_seated,
      space.max_capacity_seated_furn,
    ].some(val => val != null && val !== '' && val !== 'n/a' && Number(val) > 0);
  }
  // Fallback: suggestion metadata on the row implies capacity was present
  return !!props.row?.suggestion?.price;
});

const canSuggest = computed(
  () =>
    !!spaceIdForSuggestion.value &&
    isSuggestableUnit(form.value.unit) &&
    !!spaceTier.value &&
    spaceHasCapacity.value
);

const filteredItemOptions = computed(() => {
  const query = itemSearch.value.trim().toLowerCase();
  if (!query) return props.itemOptions;
  return props.itemOptions.filter(option =>
    option.name.toLowerCase().includes(query)
  );
});

const dialogTitle = computed(() => {
  if (isStandalone.value) return t('PRICING.ITEM_DIALOG.ADD_PRICE_TITLE');
  if (isNewItem.value) return t('PRICING.ITEM_DIALOG.CONFIGURE_TITLE');
  if (isEditingPrice.value) return t('PRICING.ITEM_DIALOG.EDIT_PRICE_TITLE');
  return t('PRICING.ITEM_DIALOG.ADD_PRICE_TITLE');
});

const confirmLabel = computed(() =>
  isEditingPrice.value
    ? t('PRICING.ITEM_DIALOG.UPDATE_BUTTON')
    : t('PRICING.ITEM_DIALOG.SAVE_BUTTON')
);

const isValid = computed(() => {
  if (form.value.price === '' || !form.value.unit || !form.value.currency)
    return false;
  if (isStandalone.value && !selectedItem.value) return false;
  return true;
});

const hasMultiplePrices = computed(() => (props.row?.prices?.length ?? 0) > 1);

const clearSuggestion = () => {
  suggestionNotes.value = [];
  suggestionError.value = '';
  suggestionResult.value = null;
};

const resetSuggestionTracking = () => {
  clearSuggestion();
  priceFromSuggestion.value = false;
};

const initializeForm = () => {
  selectedItemKey.value = '';
  itemSearch.value = '';
  showItemList.value = false;
  resetSuggestionTracking();

  if (props.price) {
    form.value = {
      name: props.price.name || '',
      unit: props.price.unit || 'per_hour',
      currency: props.price.currency || 'EUR',
      price: props.price.price ?? '',
      tax_rate: props.price.tax_rate ?? '',
      is_default: props.price.is_default ?? false,
    };
  } else {
    form.value = {
      name: '',
      unit: 'per_hour',
      currency: 'EUR',
      price: '',
      tax_rate: '',
      is_default: !(props.row?.prices?.length),
    };
  }
};

watch([() => props.row, () => props.price], initializeForm, {
  immediate: true,
});

watch(
  () => form.value.price,
  () => {
    if (applyingSuggestion.value) return;
    priceFromSuggestion.value = false;
  }
);

const fetchSuggestion = async () => {
  if (!canSuggest.value || suggesting.value) return;

  suggesting.value = true;
  clearSuggestion();

  try {
    const { data } = await SpacesAPI.suggestPrice(spaceIdForSuggestion.value, {
      unit: form.value.unit,
      currency: form.value.currency,
      tier: spaceTier.value,
    });

    if (data?.price == null) {
      suggestionError.value = t('PRICING.ITEM_DIALOG.SUGGEST_ERROR');
      return;
    }

    suggestionResult.value = data;
    suggestionNotes.value = data.warnings ? data.notes || [] : [];
  } catch (error) {
    const message =
      error?.response?.data?.error ||
      t('PRICING.ITEM_DIALOG.SUGGEST_ERROR');
    suggestionError.value = message;
  } finally {
    suggesting.value = false;
  }
};

watch(
  () => [
    form.value.unit,
    form.value.currency,
    spaceIdForSuggestion.value,
    spaceTier.value,
    spaceHasCapacity.value,
  ],
  () => {
    if (!canSuggest.value) {
      clearSuggestion();
      priceFromSuggestion.value = false;
      return;
    }
    fetchSuggestion();
  }
);

const open = () => {
  initializeForm();
  dialogRef.value?.open();
  nextTick(() => {
    if (canSuggest.value) fetchSuggestion();
  });
};

const close = () => {
  dialogRef.value?.close();
};

const selectItem = option => {
  selectedItemKey.value = option.key;
  itemSearch.value = option.name;
  showItemList.value = false;
  resetSuggestionTracking();
};

const clearSelectedItem = () => {
  selectedItemKey.value = '';
  itemSearch.value = '';
  showItemList.value = true;
  resetSuggestionTracking();
};

const applySuggestion = () => {
  if (suggestionResult.value?.price == null) return;

  applyingSuggestion.value = true;
  form.value.price = suggestionResult.value.price;
  priceFromSuggestion.value = true;
  nextTick(() => {
    applyingSuggestion.value = false;
  });
};

const handleConfirm = () => {
  if (!isValid.value) return;
  const priceAttrs = {
    id: props.price?.id || undefined,
    tier: spaceTier.value || props.price?.tier || 'none',
    name: form.value.name || null,
    unit: form.value.unit,
    currency: form.value.currency,
    price: Number(form.value.price),
    tax_rate: form.value.tax_rate !== '' ? Number(form.value.tax_rate) : null,
    is_default: form.value.is_default,
  };

  if (isStandalone.value && selectedItem.value) {
    emit('save', {
      ...selectedItem.value.sourcePayload,
      name: selectedItem.value.name,
      prices_attributes: [priceAttrs],
    });
  } else {
    emit('save', { prices_attributes: [priceAttrs] });
  }
  close();
};

const handleClose = () => {
  showItemList.value = false;
  resetSuggestionTracking();
  emit('close');
};

defineExpose({ open, close });
</script>

<template>
  <Dialog
    ref="dialogRef"
    type="edit"
    :title="dialogTitle"
    :description="t('PRICING.ITEM_DIALOG.CONFIGURE_DESCRIPTION')"
    :confirm-button-label="confirmLabel"
    :cancel-button-label="t('PRICING.CANCEL')"
    :disable-confirm-button="!isValid"
    @confirm="handleConfirm"
    @close="handleClose"
  >
    <div class="flex flex-col gap-4 p-1">
      <div
        v-if="isStandalone"
        v-on-click-outside="() => (showItemList = false)"
        class="relative"
      >
        <label class="block text-xs font-medium text-n-slate-11 mb-1">
          {{ t('PRICING.ITEM_DIALOG.ITEM_LABEL') }}
        </label>
        <div class="relative">
          <span
            class="i-lucide-search absolute left-3 top-1/2 -translate-y-1/2 size-4 text-slate-400 pointer-events-none"
          />
          <input
            v-model="itemSearch"
            type="search"
            class="reset-base w-full rounded-lg border border-n-weak bg-n-solid-2 py-2 pl-9 pr-9 text-sm"
            :placeholder="t('PRICING.ITEM_DIALOG.ITEM_PLACEHOLDER')"
            @focus="showItemList = true"
            @input="showItemList = true"
          />
          <button
            v-if="selectedItem"
            type="button"
            class="absolute right-2.5 top-1/2 -translate-y-1/2 p-0.5 rounded text-slate-400 hover:text-slate-600 transition-colors"
            :aria-label="t('PRICING.ITEM_DIALOG.ITEM_CLEAR')"
            @click="clearSelectedItem"
          >
            <Icon icon="i-lucide-x" class="size-4" />
          </button>
        </div>
        <p class="mt-1 text-xs text-n-slate-10">
          {{ t('PRICING.ITEM_DIALOG.ITEM_HINT') }}
        </p>
        <div
          v-if="showItemList"
          class="absolute left-0 right-0 top-full z-10 mt-1 max-h-48 overflow-y-auto rounded-lg border border-n-weak bg-n-solid-2 shadow-lg"
        >
          <button
            v-for="option in filteredItemOptions"
            :key="option.key"
            type="button"
            class="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-n-alpha-1 transition-colors"
            :class="{
              'bg-n-alpha-2': selectedItemKey === option.key,
            }"
            @click="selectItem(option)"
          >
            <Icon :icon="option.icon" class="size-4 shrink-0 text-n-slate-10" />
            <span class="truncate text-n-slate-12">{{ option.name }}</span>
            <span class="ml-auto shrink-0 text-xs text-n-slate-10">
              {{
                option.type === 'space'
                  ? t('PRICING.MASTER_SHEET.CATEGORY_SPACE')
                  : t('PRICING.ITEM_DIALOG.ATTRIBUTE_TYPE')
              }}
            </span>
          </button>
          <p
            v-if="filteredItemOptions.length === 0"
            class="px-3 py-2 text-sm text-n-slate-10"
          >
            {{ t('PRICING.ITEM_DIALOG.ITEM_EMPTY') }}
          </p>
        </div>
      </div>

      <div
        v-else-if="row"
        class="flex items-center gap-2 rounded-lg bg-n-alpha-1 px-3 py-2"
      >
        <span class="text-sm font-medium text-n-slate-12">
          {{ row.name }}
        </span>
      </div>

      <div>
        <label class="block text-xs font-medium text-n-slate-11 mb-1">
          {{ t('PRICING.ITEM_DIALOG.NAME_LABEL') }}
        </label>
        <input
          v-model="form.name"
          type="text"
          :placeholder="t('PRICING.ITEM_DIALOG.NAME_PLACEHOLDER')"
          class="reset-base w-full rounded-lg border border-n-weak bg-n-solid-2 px-2 py-1.5 text-sm"
        />
      </div>

      <div class="grid grid-cols-4 gap-3">
        <div>
          <label class="block text-xs font-medium text-n-slate-11 mb-1">
            {{ t('PRICING.ITEM_DIALOG.UNIT_LABEL') }}
          </label>
          <select
            v-model="form.unit"
            class="reset-base w-full rounded-lg border border-n-weak bg-n-solid-2 px-2 py-1.5 text-sm"
          >
            <option v-for="u in UNITS" :key="u" :value="u">
              {{ t(`PRICING.UNITS.${u.toUpperCase()}`) }}
            </option>
          </select>
        </div>
        <div>
          <label class="block text-xs font-medium text-n-slate-11 mb-1">
            {{ t('PRICING.ITEM_DIALOG.CURRENCY_LABEL') }}
          </label>
          <select
            v-model="form.currency"
            class="reset-base w-full rounded-lg border border-n-weak bg-n-solid-2 px-2 py-1.5 text-sm"
          >
            <option v-for="c in CURRENCIES" :key="c" :value="c">
              {{ c }}
            </option>
          </select>
        </div>
        <div>
          <label class="block text-xs font-medium text-n-slate-11 mb-1">
            {{ t('PRICING.ITEM_DIALOG.PRICE_LABEL') }}
          </label>
          <input
            v-model="form.price"
            type="number"
            step="0.01"
            min="0"
            :placeholder="t('PRICING.ITEM_DIALOG.PRICE_PLACEHOLDER')"
            class="reset-base w-full rounded-lg border border-n-weak bg-n-solid-2 px-2 py-1.5 text-sm"
          />
        </div>
        <div>
          <label class="block text-xs font-medium text-n-slate-11 mb-1">
            {{ t('PRICING.ITEM_DIALOG.TAX_RATE_LABEL') }}
          </label>
          <input
            v-model="form.tax_rate"
            type="number"
            step="0.01"
            min="0"
            max="100"
            :placeholder="t('PRICING.ITEM_DIALOG.TAX_RATE_PLACEHOLDER')"
            class="reset-base w-full rounded-lg border border-n-weak bg-n-solid-2 px-2 py-1.5 text-sm"
          />
        </div>
      </div>

      <div v-if="canSuggest" class="flex flex-col gap-2 rounded-lg border border-dashed border-n-weak bg-n-alpha-1 px-3 py-2.5">
        <div class="flex flex-wrap items-center gap-2">
          <div class="min-w-0 flex-1">
            <p class="text-xs font-medium text-n-slate-11">
              {{ t('PRICING.ITEM_DIALOG.SUGGEST_LABEL') }}
            </p>
            <p v-if="suggesting" class="text-sm text-n-slate-10">
              {{ t('PRICING.ITEM_DIALOG.SUGGEST_LOADING') }}
            </p>
            <p
              v-else-if="suggestionResult?.price != null"
              class="text-sm font-semibold text-n-slate-12"
            >
              {{ suggestionResult.price }}
              {{ suggestionResult.currency || form.currency }}
              <span
                v-if="suggestionResult.low != null && suggestionResult.high != null"
                class="ml-1 font-normal text-n-slate-10"
              >
                ({{ suggestionResult.low }} – {{ suggestionResult.high }})
              </span>
            </p>
          </div>
          <button
            v-if="suggestionResult?.price != null && !suggesting"
            type="button"
            class="inline-flex items-center gap-1.5 rounded-lg border border-n-weak bg-n-solid-2 px-2.5 py-1.5 text-xs font-medium text-n-slate-12 hover:bg-n-alpha-2 transition-colors"
            @click="applySuggestion"
          >
            <Icon icon="i-lucide-sparkles" class="size-3.5" />
            {{ t('PRICING.ITEM_DIALOG.SUGGEST_APPLY') }}
          </button>
        </div>
        <ul
          v-if="suggestionNotes.length"
          class="text-xs text-amber-700 dark:text-amber-400 list-disc pl-4 space-y-0.5"
        >
          <li v-for="(note, index) in suggestionNotes" :key="index">
            {{ note }}
          </li>
        </ul>
        <p v-if="suggestionError" class="text-xs text-red-600">
          {{ suggestionError }}
        </p>
      </div>

      <label
        v-if="hasMultiplePrices || isEditingPrice"
        class="flex items-center gap-2 cursor-pointer select-none"
      >
        <input
          v-model="form.is_default"
          type="checkbox"
          class="reset-base size-4 rounded border border-n-weak accent-n-brand"
        />
        <span class="text-sm text-n-slate-11">
          {{ t('PRICING.ITEM_DIALOG.DEFAULT_LABEL') }}
        </span>
      </label>
    </div>
  </Dialog>
</template>
